﻿<template>
  <section data-page-type="no-header">
    <Form as="div" ref="form">
      <page-spinner :spinner-url-list="spinnerUrlList" />
      <div v-if="canconvert !== null" id="quick-quote-conversion" data-page-type="flow">
        <div id="mainView" class="flow__body no-footer row container">
          <div class="small-12 medium-12 large-6 columns">
            <div v-if="canconvert === false" class="conversion">
              <h1 data-test="quickquote-conv-headline" v-html="cprFollowup.SectionTitle" />
              <p data-test="quickquote-conv-description" v-html="cprFollowup.SectionDescription" />
              <div
                v-for="quickquote in quickQuotes"
                :key="quickquote.PolicyCacheId"
                :data-test="quickquote.PolicyCacheId"
              >
                <validation-item
                  v-if="quickquote.ReadyToConvert === false"
                  :id="quickquote.PolicyCacheId"
                  class="conversion-item"
                  :data="quickquote"
                  :content="cprFollowup"
                  :scope="quickquote.PolicyCacheId.toString()"
                  @remove="removeQuickQuote"
                />
              </div>
              <div>
                <div v-html="cprFollowup.CprConsent" />
                <div class="row">
                  <div class="medium-6 small-12 columns">
                    <button
                      class="button large"
                      :disabled="isButtonDisabled"
                      data-test="cpr-followup-submit-button"
                      @click="buttonClick()"
                      v-html="cprFollowup.SubmitButtonLabel"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="canconvert">
              <h1 data-test="quickquote-conv-headline" v-html="title" />
              <p data-test="quickquote-conv-description" v-html="description" />
              <div
                v-for="(quickquote, index) in quickQuotes"
                :key="index"
                class="conversion-item"
                :data-test="quickquote.PolicyCacheId"
              >
                <conversion-item
                  :data="quickquote"
                  @conversionDone="conversionDone"
                  @conversionError="pushConversionError"
                />
              </div>
            </div>
          </div>
        </div>

        <transition name="modal">
          <v-modal
            :is-open="cprCheckMismatch"
            data-test="quickquote-cpr-mismatch-modal"
            @close="toggleOffCprMismatchModal"
          >
            <template #title>
              <h2 class="center" v-text="cprFollowup.ValidationErrorTitle" />
            </template>
            <template #content>
              <div v-html="cprFollowup.ValidationErrorDescription" />
            </template>
            <template #buttons>
              <div class="row">
                <div class="small-12 columns">
                  <button
                    class="button text-center"
                    @click="toggleOffCprMismatchModal"
                    v-text="modalLabelCloseBotton"
                  />
                </div>
              </div>
            </template>
          </v-modal>
        </transition>

        <transition v-cloak name="modal">
          <v-modal
            :is-open="showConvertionErrorModal"
            data-test="convertion-error-modal"
            @close="redirectToBasket()"
          >
            <template #title>
              <h3 v-text="modalTitle" />
            </template>
            <template #content>
              <div>
                <div v-html="modalTextTop" />
                <div v-html="textConvertionErrorModal" />
                <div v-html="modalTextBottom" />
              </div>
            </template>
            <template #buttons>
              <div>
                <button
                  class="button"
                  @click="redirectToBasket()"
                  v-text="modalLabelCloseBotton"
                />
              </div>
            </template>
          </v-modal>
        </transition>
      </div>
    </Form>
  </section>
</template>

<script lang="ts">
import { forEach } from 'lodash';
import Utility from '@/common/UtilityFunctions.js';
import ConversionItem from './Conversion/ConversionItem.vue';
import ValidationItem from './Conversion/ValidationItem.vue';
import { CprFollowup } from './Conversion/types/CprFollowup';
import {
  QuickQuoteStatus,
  readyForConversion,
  updateProposal,
  canConvert,
  deleteQuickQuote
} from './Conversion/types/QuickQuoteStatus';
import { defineComponent, PropType } from 'vue';
import PageSpinner from '@/components/UI/PageSpinner.vue';

export default defineComponent({
  name: 'QuickQuoteConversion',
  components: {
    PageSpinner,
    ConversionItem,
    ValidationItem
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      required: true
    },
    modalTitle: {
      type: String,
      required: true
    },
    modalTextTop: {
      type: String,
      required: true
    },
    modalTextBottom: {
      type: String,
      required: true
    },
    modalLabelCloseBotton: {
      type: String,
      required: true
    },
    basketUrl: {
      type: String,
      required: true
    },
    cprFollowup: {
      type: Object as PropType<CprFollowup>,
      required: true
    }
  },
  data() {
    return {
      quickQuotes: this.value.QuickQuotes as QuickQuoteStatus[],
      canconvert: null as boolean | null,
      textConvertionErrorModal: '',
      processedIds: [] as number[],
      conversionErrors: [] as number[],
      cprCheckMismatch: false,
      buttonClicked: false,
      showConvertionErrorModal: false,
      conversionTitle: this.title,
      conversionDescription: this.description
    };
  },
  computed: {
    spinnerUrlList(): string[] {
      return [
        Utility.domain() + this.basketUrl,
        Utility.constants().API.quickquote_convert_single,
        Utility.constants().API.quickquote_update_single_contact_cpr,
        Utility.constants().API.quickquote_delete,
        Utility.constants().API.quickquote_validate_single
      ];
    },
    isButtonDisabled(): boolean {
      return this.cprCheckMismatch || this.buttonClicked;
    }
  },
  created() {
    if (this.quickQuotes.length > 0) {
      canConvert(this.quickQuotes).then((result) => (this.canconvert = result));
    }
  },
  methods: {
    redirectToBasket(): void {
      location.replace(this.basketUrl);
    },
    // For tag Manager
    pushToTagManager(eventAction: any, eventLabel = ''): void {
      Utility.pushToTagManager({
        event: 'track-event',
        eventCategory: 'beregnerkurv',
        eventAction,
        eventLabel,
        eventValue: '0',
        eventNoninteraction: true
      });
    },
    showErrorModal(error: string): void {
      this.showConvertionErrorModal = true;
      this.textConvertionErrorModal = error;
    },
    async updatePromise(): Promise<void[]> {
      let updatePromise = Promise.all(
        this.quickQuotes.map(async (quickQuote) => {
          await this.$refs.form
            .validate(quickQuote.PolicyCacheId.toString())
            .then(async (result) => {
              if (result.valid) {
                if (quickQuote.ReadyToConvert !== false) return;

                await updateProposal(quickQuote)
                  .then(() => {
                    this.$refs.form.setErrors({});
                  })
                  .catch((error) => {
                    switch (error.response.status) {
                      case 401:
                      case 406:
                        this.toggleOnCprMismatchModal();
                        this.$refs.form.getErrors().add({
                          field: 'firstname',
                          msg: 'Navnet matcher ikke CPR.',
                          rule: 'name_cpr_mismatch',
                          scope: quickQuote.PolicyCacheId.toString()
                        });
                        this.$refs.form.getErrors().add({
                          field: 'lastname',
                          msg: 'Navnet matcher ikke CPR.',
                          rule: 'name_cpr_mismatch',
                          scope: quickQuote.PolicyCacheId.toString()
                        });
                        break;
                      default:
                        throw error;
                    }
                  });
              }
            });
        })
      );
      return updatePromise;
    },
    async buttonClick(): Promise<void> {
      if (Object.keys(this.$refs.form.getErrors()) === 0) {
        return;
      }

      this.buttonClicked = true;
      await this.updatePromise();
      // Any name_cpr_mismatch errors
      if (Object.keys(this.$refs.form.getErrors()).length === 0) {
        // Any field errors - missing values, cpr format etc.
        this.$refs.form.validate().then((result) => {
          // No errors - we can now validate again and see if we can convert.
          if (result && result.valid) {
            canConvert(this.quickQuotes).then(
              (canConvertResult) => (this.canconvert = canConvertResult)
            );
          }
        });
      }
      this.buttonClicked = false;
    },
    validateConversionResponse(conversionErrorResult: number[]): boolean {
      if (!conversionErrorResult.length) {
        return false;
      }

      this.textConvertionErrorModal = '';
      forEach(conversionErrorResult, (policyCacheId) => {
        let element = this.quickQuotes.find((e) => e.PolicyCacheId === policyCacheId);
        if (element) this.textConvertionErrorModal += '<p>' + element.Name + '</p>';
      });
      return true;
    },
    pushConversionError(policyCacheId: number): void {
      this.conversionErrors.push(policyCacheId);
    },
    conversionDone(policyCacheId: number): void {
      this.processedIds.push(policyCacheId);
      let remaining = this.quickQuotes.filter(
        (e) => this.processedIds.indexOf(e.PolicyCacheId) == -1
      );
      if (remaining.length === 0) {
        if (this.validateConversionResponse(this.conversionErrors)) {
          this.showErrorModal(this.textConvertionErrorModal);
        } else {
          this.redirectToBasket();
        }
      }
    },
    toggleOffCprMismatchModal(): void {
      this.cprCheckMismatch = false;
      this.buttonClicked = false;
    },
    toggleOnCprMismatchModal(): void {
      this.cprCheckMismatch = true;
    },
    removeQuickQuote(id: number): void {
      const quickQuoteWithId = (element: QuickQuoteStatus) => element.PolicyCacheId === id;
      const i = this.quickQuotes.findIndex(quickQuoteWithId);
      if (i > -1) {
        deleteQuickQuote(id)
          .then(() => {
            this.quickQuotes.splice(i, 1);
          })
          .finally(async () => {
            if (this.quickQuotes.length === 0) {
              this.redirectToBasket();
            }
            this.canconvert = readyForConversion(this.quickQuotes);
          });
      }
    }
  }
});
</script>
